import React, { useState, useEffect } from 'react';
import { UserIcon, LocationMarkerIcon } from '@heroicons/react/outline';
import ProspectDetail from './ProspectDetail';

// Approche plus robuste : un objet de mapping pour les couleurs.
// C'est le "plan" des couleurs pour chaque statut.
const STATUS_COLORS = {
  'nouveau': 'bg-blue-100 text-blue-800',
  'à traiter': 'bg-yellow-100 text-yellow-800',
  'contacté': 'bg-cyan-100 text-cyan-800',
  'à rappeler': 'bg-orange-100 text-orange-800',
  'rappel': 'bg-orange-100 text-orange-800',
  'rendez-vous pris': 'bg-green-100 text-green-800',
  'vente': 'bg-emerald-100 text-emerald-800 font-bold',
  'perdu': 'bg-red-100 text-red-800',
  'plus tard': 'bg-purple-100 text-purple-800',
  'injoignable': 'bg-pink-100 text-pink-800',
  'ne pas déranger': 'bg-gray-400 text-gray-900',
  'autre motif': 'bg-indigo-100 text-indigo-800',
  'nrp': 'bg-gray-200 text-gray-700',
  'default': 'bg-gray-100 text-gray-600' // Couleur par défaut si le statut n'est pas trouvé
};

const ProspectsList = ({ prospects: propProspects, onUpdate, users }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [prospects, setProspects] = useState(propProspects || []);

  useEffect(() => {
    if (propProspects) {
      setProspects(propProspects);
    }
  }, [propProspects]);

  const handleShowDetails = (prospect) => {
    setSelectedProspect(prospect);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProspect(null);
  };

  if (!prospects || prospects.length === 0) {
    return (
      <div className="mt-8">
        <div className="text-center text-gray-600">Aucun prospect trouvé</div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="space-y-4">
        {prospects.map((prospect, index) => {
          // 1. Déterminer le texte du statut à afficher
          const statusText = prospect.etat_du_lead || "À traiter";
          
          // 2. Nettoyer le statut pour la recherche dans le plan (ignorer casse et espaces)
          const statusKey = statusText.trim().toLowerCase();
          
          // 3. Trouver la couleur correspondante dans le plan, ou utiliser la couleur par défaut
          const colorClass = STATUS_COLORS[statusKey] || STATUS_COLORS.default;

          return (
            <div key={prospect.id || index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col md:flex-row items-center gap-4">

                {/* Info principale (gauche) */}
                <div className="w-full md:w-3/12">
                  <div className="text-blue-600 font-medium text-lg mb-1 truncate" title={prospect.projet_google || prospect.projet || "Projet non défini"}>
                    {prospect.projet_google || prospect.projet || "Projet non défini"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Source: {prospect.source || "Inconnue"}
                  </div>
                </div>
                
                {/* Infos contact (milieu) */}
                <div className="w-full md:w-4/12">
                  <div className="flex items-center truncate">
                    <UserIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                    <span className="font-medium text-gray-900">{prospect.nom} {prospect.prenom}</span>
                  </div>
                  <div className="flex items-center truncate">
                    <LocationMarkerIcon className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{prospect.code_postal} {prospect.ville}</span>
                  </div>
                </div>
                
                {/* Statut (centre) */}
                <div className="w-full md:w-3/12 flex justify-center">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
                    {statusText}
                  </span>
                </div>
                
                {/* Bouton (droite) */}
                <div className="w-full md:w-2/12 flex justify-end">
                  <button 
                    onClick={() => handleShowDetails(prospect)}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors flex-shrink-0"
                  >
                    Détails
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && selectedProspect && (
        <ProspectDetail
          prospect={selectedProspect}
          onClose={handleCloseModal}
          onUpdate={onUpdate}
          users={users}
        />
      )}
    </div>
  );
};

export default ProspectsList;